import React from "react";
import { HoverDeleteButton } from "@scripts/react/components/Button";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { MediaItemFileType } from "@/types/media";

/**
 * Full loading progress for single file view - shows text, progress bar, and percentage
 */
function LoadingProgressInfo({
    uploadPhaseText,
    uploadProgress
}: {
    uploadPhaseText?: string;
    uploadProgress?: number;
}) {
    return (
        <div className="flex w-full flex-col items-center gap-1">
            {uploadPhaseText && <span className="text-xs text-tertiary">{uploadPhaseText}</span>}
            {uploadProgress !== undefined && uploadProgress >= 0 && (
                <>
                    <div className="h-1 w-full overflow-hidden rounded-full bg-gray-200">
                        <div
                            className="h-full bg-brand transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                        />
                    </div>
                    <span className="text-xs text-tertiary">{uploadProgress}%</span>
                </>
            )}
        </div>
    );
}

/**
 * Shared component for rendering thumbnail content (image/spinner/icon) with container
 * Used by both FileSwitcher and FileGridPreview
 */
export function ThumbnailContent({
    thumbnail,
    isUploading,
    fileType,
    fileName,
    fileId,
    className,
    title,
    onClick,
    onDoubleClick,
    onDelete,
    onEdit,
    isShareMode = false,
    isSelected = false,
    flowLocked = false,
    showOverlay = true,
    insideGrid = false,
    showPlayButton = false,
    isSingleFile = false,
    uploadProgress,
    uploadPhaseText,
    isApproved = false
}: {
    thumbnail: string | undefined;
    isUploading: boolean;
    fileType: MediaItemFileType;
    fileName: string;
    fileId?: string;
    className?: string;
    title?: string;
    onClick?: (evt: React.MouseEvent) => void;
    onDoubleClick?: (evt: React.MouseEvent) => void;
    onDelete?: (evt: React.MouseEvent) => void;
    onEdit?: (evt: React.MouseEvent) => void;
    isShareMode?: boolean;
    isSelected?: boolean;
    flowLocked?: boolean;
    showOverlay?: boolean;
    insideGrid?: boolean;
    showPlayButton?: boolean;
    isSingleFile?: boolean;
    uploadProgress?: number;
    uploadPhaseText?: string;
    isApproved?: boolean;
}) {
    const imageClassName = "h-full w-full rounded-md object-cover";
    const spinnerClassName = "fa fa-spinner fa-spin text-sm text-gray-500";
    const iconClassName = `fa ${fileType === "video" ? "fa-spinner fa-spin" : "fa-image"} text-sm text-gray-500`;

    const selectedClasses = twClassNames(
        flowLocked && "cursor-not-allowed opacity-70 ring-1",
        isSelected && "ring-2",
        !isSelected && !flowLocked && "cursor-pointer ring-1 hover:ring-2",
        isShareMode && {
            "h-12 w-12": true,
            "ring-gray-500": flowLocked,
            "ring-purboflow-300": isSelected,
            "ring-gray-500 hover:ring-gray-300": !isSelected && !flowLocked
        },
        !isShareMode && {
            "insideGrid": insideGrid,
            "ring-gray-300": flowLocked,
            "ring-brand": isSelected,
            "ring-gray-300 hover:ring-gray-400": !isSelected && !flowLocked
        }
    );
    let content;
    if (thumbnail) {
        content = (
            <div className="relative h-full w-full">
                <img src={thumbnail} alt={fileName} className={imageClassName} />
                {isUploading && (
                    <div className="pointer-events-none absolute inset-0 flex h-full w-full items-center justify-center rounded-md bg-black/30 text-white shadow-md">
                        <i className="fas fa-spinner fa-spin text-xl text-white" />
                    </div>
                )}
                {isApproved && !isUploading && (
                    <div className="pointer-events-none absolute inset-0 flex h-full w-full items-center justify-center rounded-md bg-black/50">
                        <i className="fas fa-check text-xl text-white" />
                    </div>
                )}
            </div>
        );
    } else {
        const icon = isUploading ? spinnerClassName : iconClassName;

        content = (
            <div
                className={twClassNames(
                    "flex h-full flex-col items-center justify-center",
                    isSingleFile ? "gap-2 p-2" : "gap-0.5 p-1"
                )}
            >
                {!isSingleFile && isUploading && <i className={icon} />}
                {isSingleFile && isUploading && (
                    <LoadingProgressInfo
                        uploadPhaseText={uploadPhaseText}
                        uploadProgress={uploadProgress}
                    />
                )}
            </div>
        );
    }

    return (
        <div
            className={twClassNames("fileThumbnail group", selectedClasses, className)}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            title={title}
            aria-label={title || fileName}
            data-file-id={fileId}
        >
            {content}
            {/* Hover action buttons container - positioned at top-right */}
            {!isUploading && (onEdit || onDelete) && (
                <div className="absolute -right-1 -top-1 z-10 flex items-center gap-1 opacity-0 transition duration-75 group-hover:opacity-100">
                    {onEdit && (
                        <button
                            onClick={onEdit}
                            className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-full bg-gray-600 text-xs text-white shadow-sm ring-1 ring-inset ring-white ring-opacity-50 transition duration-75 hover:bg-gray-500 focus:outline-none"
                            aria-label={`Rename ${fileName}`}
                        >
                            <i className="far fa-pencil text-[8px]" />
                        </button>
                    )}
                    {onDelete && (
                        <HoverDeleteButton
                            onClick={onDelete}
                            ariaLabel={`Delete ${fileName}`}
                            className="!relative !opacity-100"
                        />
                    )}
                </div>
            )}
            {showOverlay && (
                <div className={twClassNames("thumbnailOverlay", isSelected && "selected")} />
            )}
            {showPlayButton && fileType === "video" && thumbnail && !isUploading && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-black bg-opacity-60 backdrop-blur-sm transition-all duration-200 group-hover:scale-110 group-hover:bg-opacity-80">
                        <i className="fa fa-play ml-1 text-2xl text-white" />
                    </div>
                </div>
            )}
        </div>
    );
}
